'use client'

import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { usePathname } from 'next/navigation'
import { usePageEditorStore } from '@/store/pageEditorStore'
import styles from './WorkPageTitle.module.scss'
import { ComponentProps } from '@/types/database'
import { usePreloader } from '@/components/PagePreloader/PreloaderContext'
import { sanitizeBasicHtml } from '@/lib/utils/html-sanitizer'


interface WorkPageTitleProps extends ComponentProps {
  client?: string
  description?: string
  services?: string
  director?: string
  directorLabel?: string
  headingLevel?: 'h1' | 'h2'
  // Internal props passed from renderComponent for front-end rendering
  _pageTitle?: string
  _workClient?: string
  _workServices?: string
  _workDirector?: string
  _workDirectorLabel?: string
  noTopPadding?: boolean
  noBottomPadding?: boolean
}

export const WorkPageTitle: React.FC<WorkPageTitleProps> = ({
  client = 'Client Name',
  description = 'Project description',
  services = '',
  director = '',
  directorLabel = '',
  headingLevel = 'h1',
  _pageTitle,
  _workClient,
  _workServices,
  _workDirector,
  _workDirectorLabel,
  noTopPadding = false,
  noBottomPadding = false
}) => {
  const { currentPage, updatePageSettings } = usePageEditorStore()
  const HeadingTag = headingLevel
  
  // Animation states
  const [titleInView, setTitleInView] = useState(false)
  const [metaInView, setMetaInView] = useState(false)
  const [descriptionInView, setDescriptionInView] = useState(false)
  const [servicesInView, setServicesInView] = useState(false)
  
  // Refs for intersection observers
  const titleRef = useRef<HTMLHeadingElement>(null)
  const metaRef = useRef<HTMLDivElement>(null)
  const descriptionRef = useRef<HTMLParagraphElement>(null)
  const servicesRef = useRef<HTMLDivElement>(null)
  
  const pathname = usePathname()
  const isAdminMode = pathname.startsWith('/admin')
  const { state: preloaderState, isInitialLoad } = usePreloader()
  
  // Check if animations should be enabled
  const canAnimate = !isAdminMode && preloaderState === 'hidden' && !isInitialLoad



  // Title intersection observer
  useEffect(() => {
    const element = titleRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setTitleInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setTitleInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Meta intersection observer
  useEffect(() => {
    const element = metaRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setMetaInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setMetaInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Description intersection observer
  useEffect(() => {
    const element = descriptionRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setDescriptionInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setDescriptionInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Services intersection observer
  useEffect(() => {
    const element = servicesRef.current
    
    if (!element || isAdminMode) {
      if (isAdminMode) setServicesInView(true)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry.isIntersecting && canAnimate) {

          setServicesInView(true)
        }
      },
      { threshold: 0.3 }
    )

    observer.observe(element)
    return () => observer.unobserve(element)
  }, [canAnimate, isAdminMode])

  // Use front-end data if available (passed from renderComponent), otherwise use admin editor data
  const pageTitle = _pageTitle || currentPage?.title || 'Work Title'
  const workClient = _workClient || currentPage?.seo?.workClient || client
  
  // Always prioritize page-level services over component-level services
  const workServices = _workServices || currentPage?.seo?.workServices || services
  const workDirector = _workDirector || currentPage?.seo?.workDirector || director
  const workDirectorLabel =
    _workDirectorLabel ?? currentPage?.seo?.workDirectorLabel ?? directorLabel
  const directorCreditText = workDirector
    ? `${workDirectorLabel?.trim() || 'Director'} ${workDirector}`.trim()
    : ''

  // Migration: If component has services but page SEO doesn't, migrate them on mount
  useEffect(() => {
    if (!isAdminMode || !currentPage) return

    const seoUpdates: typeof currentPage.seo = {}

    if (services && !currentPage?.seo?.workServices) {
      seoUpdates.workServices = services
    }

    if (director && !currentPage?.seo?.workDirector) {
      seoUpdates.workDirector = director
    }

    if (Object.keys(seoUpdates).length > 0) {
      updatePageSettings({
        seo: {
          ...currentPage.seo,
          ...seoUpdates
        }
      })
    }
  }, [isAdminMode, currentPage, services, director, updatePageSettings]) // Include all dependencies




  const titleClasses = [
    styles['work-page-title__title'],
    styles[`work-page-title__title--${headingLevel}`]
  ].filter(Boolean).join(' ')

  const sectionClasses = [
    styles['work-page-title'],
    styles['work-page-title--center']
  ].filter(Boolean).join(' ')

  return (
    <section className={sectionClasses}>
      <div className={`${styles['work-page-title__container']} ${
        noTopPadding && noBottomPadding ? styles['work-page-title__container--no-vertical'] :
        noTopPadding ? styles['work-page-title__container--no-top'] :
        noBottomPadding ? styles['work-page-title__container--no-bottom'] : ''
      }`}>
        <motion.div
          ref={titleRef}
          initial={{ opacity: isAdminMode ? 1 : 0 }}
          animate={{ opacity: titleInView ? 1 : 0 }}
          transition={{ 
            duration: 0.8,
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          <HeadingTag 
            className={titleClasses}
            dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(pageTitle) }}
          />
        </motion.div>
        
        <motion.div 
          ref={metaRef}
          className={styles['work-page-title__meta']}
          initial={{ opacity: isAdminMode ? 1 : 0 }}
          animate={{ opacity: metaInView ? 1 : 0 }}
          transition={{ 
            duration: 0.8,
            delay: 0.2, // Appears after title
            ease: [0.25, 0.46, 0.45, 0.94]
          }}
        >
          {workClient && (
            <span className={styles['work-page-title__client']}>
              {workClient}
            </span>
          )}
        </motion.div>
          
        
        {description && (
          <motion.p 
            ref={descriptionRef}
            className={styles['work-page-title__description']}
            initial={{ opacity: isAdminMode ? 1 : 0 }}
            animate={{ opacity: descriptionInView ? 1 : 0 }}
            transition={{ 
              duration: 0.8,
              delay: 0.4, // Appears after meta
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(description) }}
          />
        )}

        {(workServices || workDirector) && (
          <motion.div 
            ref={servicesRef}
            className={styles['work-page-title__services']}
            initial={{ opacity: isAdminMode ? 1 : 0 }}
            animate={{ opacity: servicesInView ? 1 : 0 }}
            transition={{ 
              duration: 0.8,
              delay: 0.6, // Appears after description
              ease: [0.25, 0.46, 0.45, 0.94]
            }}
          >
            {workServices && (
              <span className={styles['work-page-title__services-text']}>
                {workServices}
              </span>
            )}
            {workDirector && (
              <span className={styles['work-page-title__director-text']}>
                {directorCreditText}
              </span>
            )}
          </motion.div>
        )}
      </div>
    </section>
  )
} 